import { Http, HttpApi, HttpEndpoint, NetworkClient } from "@kosmod/pancake";
import { AssetClient, AssetID, assetsAPI, AssetURLMap, AssetMeta } from "../defs";
import { useFrontendCore } from "@kosmod/core";
import { useEffect, useMemo, useRef } from "@lukekaalim/act";
import { GameID } from "@kosmod/sesame";

export type GameAssetStrategy =
  | { type: 'api-url' } // Build valid AssetAPI URls
  | { type: 'local-access', assets: Map<AssetID, Uint8Array<ArrayBuffer>> } // we have magic access to all the assets bytes

/**
 * Resolves assets (for a particular Sesame.GameID)
 * and retrieves URLs to download them, or retrieves
 * (buffers/streams) themselves using a builtin HTMLClient.
 */
export type GameAssetClient = {
  /**
   * Calculates the URL needed to fetch this resource
   * from this client.
   * @param asset 
   */
  getAssetURL(asset: AssetID): URL,

  getAssetStream(asset: AssetID): Promise<null | ReadableStream>,
  getAssetBlob(asset: AssetID):   Promise<null | Blob>,
  getAssetBuffer(asset: AssetID): Promise<null | Uint8Array<ArrayBuffer>>,
  uploadBlob(blob: Blob): Promise<AssetID>,

  api: AssetClient,
};

export const useGameAssetClientProvider = <T extends null | GameID>(
  game: T,
  strategy: GameAssetStrategy,
  net: NetworkClient,
  urlMap: AssetURLMap,
): T extends null ? null | GameAssetClient : GameAssetClient => {
  
  // only written to in localBlob strategy
  const blobURLs = useRef(new Map<AssetID, URL>()).get()

  useEffect(() => {
    return () => {
      // when unmounting useGameAssetClientProvider,
      // revoke all blob URLs to avoid memory leaks 
      for (const url of blobURLs.values())
        URL.revokeObjectURL(url.href)
    }
  }, [game])

  return useMemo(() => {
    if (!game)
      // Some crazy work here lol...
      return null as null | GameAssetClient as GameAssetClient;
    
    const getAssetURL = (asset: AssetID) => {
      switch (strategy.type) {
        case 'api-url':
          const query: HttpEndpoint.Query<typeof assetsAPI.paths['/']['methods']['GET']['query']> = {
            asset,
            game,
          }
          const url = HttpEndpoint.createURL(urlMap["/"], query)
          return url;
        case 'local-access':
          if (blobURLs.has(asset))
            return blobURLs.get(asset) as URL;

          if (strategy.assets.has(asset)) {
            const bytes = strategy.assets.get(asset) as Uint8Array<ArrayBuffer>;
            const blob = new Blob([bytes]);
            const blobURL = new URL(URL.createObjectURL(blob));
            blobURLs.set(asset, blobURL)
            return blobURL;
          }

          throw new Error(`Cannot build Blob URL for asset that does not exist yet`)
      }
    };

    const api = HttpApi.createClientFromURLMap(assetsAPI, urlMap, net)

    return {
      getAssetURL,

      async getAssetStream(asset) {
        const response = await net.http.request(getAssetURL(asset), {})
        return response.body;
      },
      async getAssetBlob(asset) {
        const response = await net.http.request(getAssetURL(asset), {})
        return await new Response(response.body).blob();
      },
      async getAssetBuffer(asset) {
        const response = await net.http.request(getAssetURL(asset), {})
        return await new Response(response.body).bytes();
      },
      async uploadBlob(blob) {
        const contentLength = blob.size;
        const contentType = blob.type;
        const response = await api["/"].POST({ body: { contentLength, contentType, game: game } })

        const asset = response.body.assetId;
        await api["/upload"].PUT({ query: { asset, game }, body: blob.stream() })

        return asset;
      },

      api,
    }
  }, [net])
};
